import React, { useLayoutEffect, useState, useRef } from "react";

import * as am5 from "@amcharts/amcharts5";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import * as am5xy from "@amcharts/amcharts5/xy";
import { ExpandLess, ExpandMore, FiberManualRecord } from "@mui/icons-material";
import { Menu, MenuItem, ToggleButton, ToggleButtonGroup } from "@mui/material";
import InfoDialog from "components/InfoDialog";
import dayjs from "dayjs";
import { useSearchParams } from "react-router-dom";

import { ChartLoader } from "../../components/ChartLoader";
import { COMMENTS_TRENDS } from "../../constants";

const FavouribilityChart = ({ data, isLoading }) => {
  const [view, setView] = useState("absolute");
  const chartRef = useRef(null);
  const [currentParams, setCurrentParams] = useSearchParams();

  const trendParam = currentParams.get("trend");

  const currentTrend = trendParam ? trendParam : COMMENTS_TRENDS.FAVORABILITY;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  
  const favorabilityData = Object.entries(data ?? {}).map(([date, value]) => {
    const favorability = value.favorability[0];
    const sum = favorability.Favorable + favorability.Neutral + favorability.Unfavorable;
    return {
      year: date,
      Favorable: view === "absolute" ? favorability.Favorable : Math.round((favorability.Favorable / sum) * 100),
      Unfavorable: view === "absolute" ? favorability.Unfavorable : Math.round((favorability.Unfavorable / sum) * 100),
      Neutral: view === "absolute" ? favorability.Neutral : Math.round((favorability.Neutral / sum) * 100),
      total: value.total_comments,
    };
  }).filter((item) => item.total > 0);

  const sentimentData = Object.entries(data ?? {}).map(([date, value]) => {
    const sentiment = value?.sentiment?.[0];
    if (!sentiment) {
      return {
        total: 0,
      };
    }
    const sum = sentiment.positive + sentiment.neutral + sentiment.negative;
    return {
      year: date,
      Positive: view === "absolute" ? sentiment.positive : Math.round((sentiment.positive / sum) * 100),
      Neutral: view === "absolute" ? sentiment.neutral : Math.round((sentiment.neutral / sum) * 100),
      Negative: view === "absolute" ? sentiment.negative : Math.round((sentiment.negative / sum) * 100),
      total: value.total_comments,
    };
  }).filter((item) => item.total > 0);

  const chartData = currentTrend === COMMENTS_TRENDS.FAVORABILITY ? favorabilityData : sentimentData;

  chartData.sort((a, b) => dayjs(a.year).diff(b.year));

  const handleClick = (event) => {
    if (sentimentData.length > 0) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMenuItemClick = (trend) => {
    currentParams.set("trend", trend);
    setCurrentParams(currentParams);
    handleClose();
  };
  

  useLayoutEffect(() => {
    let root = null;
    if (chartData.length > 0) {
      root = am5.Root.new("chartdiv");
      root._logo.dispose();

      const theme = am5.Theme.new(root);
      theme.rule("ColorSet").setAll({
        colors: [
          am5.color("#94CBB1"),
          am5.color("#DFDFDF"),
          am5.color("#EF99B3"),
        ],
        reuse: true
      });

      root.setThemes([am5themes_Animated.new(root), theme]);

      let chart = root.container.children.push(
        am5xy.XYChart.new(root, {})
      );

      let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
      cursor.lineY.set("visible", false);

      let xRenderer = am5xy.AxisRendererX.new(root, { minGridDistance: 30 });

      xRenderer.setAll({
        cellStartLocation: 0.2,
        cellEndLocation: 0.8,
      });

      xRenderer.labels.template.setAll({
        fill: am5.color("#666666"),
        textAlign: "center",
        fontSize: 12,
      });

      let xAxis = chart.xAxes.push(
        am5xy.CategoryAxis.new(root, {
          maxDeviation: 0.3,
          categoryField: "year",
          renderer: xRenderer,
        })
      );

      let yAxis = chart.yAxes.push(
        am5xy.ValueAxis.new(root, {
          maxDeviation: 0.3,
          renderer: am5xy.AxisRendererY.new(root, {}),
        })
      );

      yAxis.get("renderer").labels.template.setAll({
        fill: am5.color("#666666"),
        textAlign: "center",
        fontSize: 12,
      });

      xAxis.data.setAll(chartData);

      const makeSeries = (name, valueYField) => {
        const series = chart.series.push(
          am5xy.ColumnSeries.new(root, {
            name,
            xAxis,
            yAxis,
            valueYField,
            categoryXField: "year",
          })
        );

        series.columns.template.setAll({
          tooltipText: `{name}: {valueY}${view === "absolute" ? "" : "%"}`,
          width: am5.percent(100),
          cornerRadiusTL: 5,
          cornerRadiusTR: 5
        });

        series.bullets.push(function () {
          return am5.Bullet.new(root, {
            locationX: 0.5,
            locationY: 1.01,
            sprite: am5.Label.new(root, {
              centerX: am5.p50,
              centerY: am5.p50,
              text: "{valueY}",
              fill: am5.color("#666666"),
              fontSize: 12,
              populateText: true
            })
          });
        });

        series.columns.template.onPrivate("height", function(height, target) {
          am5.array.each(target.dataItem.bullets, function(bullet) {
            if (height > 50) {
              bullet.set("locationY", 0.6);
              bullet.get("sprite").set("centerY", am5.p50);
              bullet.get("sprite").set("fill", am5.color("#000"));
            } else {
              bullet.set("locationY", 1);
              bullet.get("sprite").set("centerY", am5.p100);
            }
          });
        });

        series.data.setAll(chartData);
      };

      if (currentTrend === COMMENTS_TRENDS.FAVORABILITY) {
        makeSeries("Favorable", "Favorable");
        makeSeries("Neutral", "Neutral");
        makeSeries("Unfavorable", "Unfavorable");
      } else {
        makeSeries("Positive", "Positive");
        makeSeries("Neutral", "Neutral");
        makeSeries("Negative", "Negative");
      }

      xAxis.get("renderer").labels.template.adapters.add("text", function (text, target) {
        let dataItem = target.dataItem;
        if (dataItem) {
          let dataContext = dataItem.dataContext;
          return dayjs(dataContext.year).format("MMM 'YY") + "\n" + dataContext.total + " comments";
        }
        return text;
      });

      chartRef.current = chart;
    }

    return () => {
      root?.dispose();
    };
    
  }, [view, chartData.length, currentTrend]);

  if (isLoading && chartData.length === 0) {
    return <ChartLoader />;
  }

  if (!isLoading && chartData.length === 0) {
    return null;
  }

  return <div>
    <div className="flex justify-between items-center">
      <h2
        aria-controls="trend-menu"
        className="text-xl font-extrabold capitalize cursor-pointer" 
        onClick={handleClick}>
        {currentTrend} trend 
        {sentimentData.length === 0 ? null : open ? <ExpandLess /> : <ExpandMore />}
      </h2>
      <Menu
        id="trend-menu"
        anchorEl={anchorEl}
        keepMounted
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <MenuItem key={COMMENTS_TRENDS.FAVORABILITY}
          onClick={() => handleMenuItemClick(COMMENTS_TRENDS.FAVORABILITY)}
          selected={currentTrend === COMMENTS_TRENDS.FAVORABILITY}
        >
          Favorability trend
        </MenuItem>

        <MenuItem key={COMMENTS_TRENDS.SENTIMENT}
          onClick={() => handleMenuItemClick(COMMENTS_TRENDS.SENTIMENT)}
          selected={currentTrend === COMMENTS_TRENDS.SENTIMENT}
        >
          Sentiment trend
        </MenuItem>
        
      </Menu>
      <ToggleButtonGroup value={view} exclusive size="small">
        <ToggleButton value="absolute" onClick={() => setView("absolute")}>Absolute</ToggleButton>
        <ToggleButton value="percentage" onClick={() => setView("percentage")}>Percentage</ToggleButton>
      </ToggleButtonGroup>
    </div>
        
    {
      sentimentData.length > 0 ? <InfoDialog 
        triggerComponent={<div className="underline cursor-pointer"> What is the difference between favorability and sentiment ?</div>}
        title="Favorability vs Sentiment"
        description={<div className="flex flex-col gap-4">
          <div>Favorability provides the distribution of quantitative responses into favorable (Score 4 or 5), neutral (score 3) or unfavorable (score 1 or 2).</div>
          <div>Sentiment is the underlying emotional tone expressed in a comment, categorized as positive, negative, or neutral.</div>
        </div>}
      /> 
        : null
    }
    
    <div className="flex justify-between items-center mt-2">
      {
        currentTrend === COMMENTS_TRENDS.FAVORABILITY ?
          <div className="flex items-center">
            <FiberManualRecord sx={{ color: "#94CBB1" }} />
            <p className="text-[#666666] text-xs mr-6">Favorable</p>
            <FiberManualRecord sx={{ color: "#DFDFDF" }} />
            <p className="text-[#666666] text-xs mr-6">Neutral</p>
            <FiberManualRecord sx={{ color: "#EF99B3" }} />
            <p className="text-[#666666] text-xs">Unfavorable</p>
          </div>
          : 
          <div className="flex items-center">
            <FiberManualRecord sx={{ color: "#94CBB1" }} />
            <p className="text-[#666666] text-xs mr-6">Positive</p>
            <FiberManualRecord sx={{ color: "#DFDFDF" }} />
            <p className="text-[#666666] text-xs mr-6">Neutral</p>
            <FiberManualRecord sx={{ color: "#EF99B3" }} />
            <p className="text-[#666666] text-xs">Negative</p>
          </div>
      }
      <p className="text-[#666666] text-xs">Excluding ratings from “what-else” question</p>
    </div>
    <div id="chartdiv" style={{ width: "80%", height: "500px", marginTop: "40px", marginLeft: "auto", marginRight: "auto" }}></div>
    <p className="text-xs text-[#666666] mt-2 text-center">
      *The numbers that appear in the bar chart above represent the comments analyze. This tool only considers comments in the english language, therefore the number represented in this tool may differ from the tool the vendor (Glint) offers.
    </p>
  </div>;
};

export default FavouribilityChart;
